/**
 * Attachment Display Component
 * Renders all attachments for a message
 */

import { useState } from 'react';
import type { MessageAttachment } from '@/types/api.types';
import { getAttachmentUrl } from '@/services/attachments.service';
import ImageAttachment from './ImageAttachment';
import VideoAttachment from './VideoAttachment';
import DocumentAttachment from './DocumentAttachment';
import MediaGallery, { type MediaItem } from './MediaGallery';

interface AttachmentDisplayProps {
  attachments: MessageAttachment[];
}

const AttachmentDisplay = ({ attachments }: AttachmentDisplayProps) => {
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  if (!attachments || attachments.length === 0) return null;

  // Infer display_type from mime_type when not set (e.g. after page reload)
  const getDisplayType = (a: MessageAttachment): string => {
    if (a.display_type) return a.display_type;
    if (a.mime_type?.startsWith('image/')) return 'image';
    if (a.mime_type?.startsWith('video/')) return 'video';
    return 'file';
  };

  // Separate attachments by display_type
  const imageDisplays = attachments.filter((a) => getDisplayType(a) === 'image');
  const videos = attachments.filter((a) => getDisplayType(a) === 'video');
  const fileDownloads = attachments.filter((a) => getDisplayType(a) === 'file');

  // Unified media items for gallery navigation (images first, then videos — matching render order)
  const mediaItems: MediaItem[] = [
    ...imageDisplays.map((a) => ({
      id: a.id,
      url: getAttachmentUrl(a.id),
      fileName: a.file_name,
      mimeType: a.mime_type,
      type: 'image' as const,
    })),
    ...videos.map((a) => ({
      id: a.id,
      url: getAttachmentUrl(a.id),
      fileName: a.file_name,
      mimeType: a.mime_type,
      type: 'video' as const,
    })),
  ];

  const openGallery = (attachmentId: string) => {
    const idx = mediaItems.findIndex((item) => item.id === attachmentId);
    if (idx !== -1) setGalleryIndex(idx);
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Image displays (display_type = 'image') - Full inline preview */}
      {imageDisplays.length > 0 && (
        <div
          className={`grid gap-2 ${
            imageDisplays.length === 1
              ? 'grid-cols-1'
              : imageDisplays.length === 2
              ? 'grid-cols-2'
              : 'grid-cols-2 md:grid-cols-3'
          }`}
        >
          {imageDisplays.map((attachment) => (
            <ImageAttachment
              key={attachment.id}
              attachment={attachment}
              onLightboxOpen={openGallery}
            />
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.map((attachment) => (
        <VideoAttachment
          key={attachment.id}
          attachment={attachment}
          onGalleryOpen={openGallery}
        />
      ))}

      {/* File downloads (display_type = 'file') - Small thumbnail + download button */}
      {fileDownloads.map((attachment) => (
        <DocumentAttachment key={attachment.id} attachment={attachment} />
      ))}

      {/* Media gallery viewer */}
      {galleryIndex !== null && (
        <MediaGallery
          items={mediaItems}
          initialIndex={galleryIndex}
          onClose={() => setGalleryIndex(null)}
        />
      )}
    </div>
  );
};

export default AttachmentDisplay;
